import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Address, Cell } from '@ton/core';
import {
  MAX_FEE_BPS,
  buildAcceptAdmin,
  buildSetCollection,
  buildSetFee,
  buildSetNextAdmin,
  buildSetRefPubkey,
  buildWithdrawFees,
  detectItemLayout,
  toTxMessage,
  type ItemLayout,
} from '@78/core';
import { fetchReferralPubkey, fetchRegistry, fetchStats } from '../lib/api.ts';
import {
  cellFromBoc,
  getAccountCode,
  getTonBalance,
  getNftIndex,
  getMasterNextAdmin,
  getMasterRefPubkey,
} from '../lib/chain.ts';
import { useActions } from '../lib/actions.ts';
import { sameAddress, useCollectionNames, shortAddress } from '../lib/collections.ts';
import { useErrorText } from '../lib/errors.ts';
import { Money, useFmt } from '../lib/format.tsx';
import { Sheet } from '../components/Sheet.tsx';

/** Газ на админское сообщение мастеру: он только меняет своё хранилище. */
const ADMIN_MESSAGE_TON = 50_000_000n;

const FIELD: React.CSSProperties = {
  background: 'var(--sf2)',
  border: '1px solid var(--line)',
  color: 'var(--ink)',
  borderRadius: 10,
  padding: 10,
  fontSize: 13,
};

/**
 * Добавление в реестр.
 *
 * Код берётся из уже развёрнутого контракта — айтема коллекции или кошелька
 * валюты. Просить его BOC'ом от руки было бы честнее только формально: admin
 * всё равно взял бы его из того же места, но с шансом ошибиться при
 * копировании.
 */
function AddToRegistry({
  onSend,
}: {
  onSend: (address: string, code: Cell, layout: ItemLayout) => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [address, setAddress] = useState('');
  const [sample, setSample] = useState('');
  const [reading, setReading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ code: Cell; layout: ItemLayout } | null>(null);

  /**
   * Читает код образца айтема и определяет раскладку коллекции.
   *
   * Раскладку нельзя спросить — её можно только проверить: адрес считается
   * каждой по очереди и сравнивается с настоящим айтемом. Не сошлась ни
   * одна — вносить коллекцию нельзя, сделку по ней всё равно не создать, и
   * узнать об этом лучше здесь, чем после оплаченного газа.
   */
  const read = () => {
    setErr(null);
    setReading(true);
    void (async () => {
      const code = cellFromBoc(await getAccountCode(sample));

      const index = await getNftIndex(sample);
      const layout = detectItemLayout({
        code,
        index,
        collection: Address.parse(address),
        item: Address.parse(sample),
      });
      if (layout === null) throw new Error('unknown_layout');
      return { code, layout };
    })()
      .then(setConfirm)
      .catch((e: unknown) =>
        setErr(e instanceof Error && e.message === 'unknown_layout'
          ? t('admin.layout_unknown')
          : errorText(e) || t('admin.no_code')),
      )
      .finally(() => setReading(false));
  };

  return (
    <div className="col" style={{ gap: 8 }}>
      <input
        className="mono"
        style={FIELD}
        placeholder={t('admin.collection_address')}
        value={address}
        onChange={(e) => setAddress(e.target.value.trim())}
      />
      <input
        className="mono"
        style={FIELD}
        placeholder={t('admin.item_address')}
        value={sample}
        onChange={(e) => setSample(e.target.value.trim())}
      />
      <span className="muted" style={{ fontSize: 12 }}>
        {t('admin.item_hint')}
      </span>

      {err !== null && <div className="danger" style={{ fontSize: 13 }}>{err}</div>}

      {confirm !== null && (
        <Sheet
          title={t('confirm.registry_add')}
          confirmLabel={t('admin.add_collection')}
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            onSend(address, confirm.code, confirm.layout);
            setConfirm(null);
            setAddress('');
            setSample('');
          }}
        >
          <span className="muted" style={{ fontSize: 13 }}>{t('confirm.registry_add_body')}</span>
          <span className="mono" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{address}</span>
          <span className="label" style={{ color: 'var(--lime-text)' }}>
            {t('admin.layout_detected')}:{' '}
            {LAYOUT_NAME[confirm.layout]}
          </span>
        </Sheet>
      )}

      <button
        className="btn"
        disabled={reading || address.length === 0 || sample.length === 0}
        onClick={read}
      >
        {reading ? t('admin.reading_code') : t('admin.add_collection')}
      </button>
    </div>
  );
}

/**
 * Передача администрирования.
 *
 * Права у мастера невелики, но без них он мёртв: ни комиссии, ни реестров,
 * ни вывода собранного. Поэтому передача в два шага — предложение и
 * согласие, — и поэтому же карточка показывает незавершённую передачу обеим
 * сторонам: второй шаг делает не тот, кто начал.
 */
function Handover({
  masterAddress,
  admin,
  myAddress,
  connected,
  onSend,
}: {
  masterAddress: string;
  admin: string | null;
  myAddress: string;
  connected: boolean;
  onSend: (body: Parameters<typeof toTxMessage>[2]) => void;
}) {
  const { t } = useTranslation();
  const [heir, setHeir] = useState('');
  const [confirm, setConfirm] = useState<'offer' | 'cancel' | 'accept' | null>(null);

  const next = useQuery({
    queryKey: ['masterNextAdmin', masterAddress],
    queryFn: () => getMasterNextAdmin(masterAddress),
    enabled: masterAddress.length > 0,
    retry: false,
  });
  const offered = next.data ?? null;
  const mine = offered !== null && myAddress.length > 0 && sameAddress(offered, myAddress);

  return (
    <div className="card col">
      <div className="label">{t('admin.handover')}</div>
      <span className="muted" style={{ fontSize: 12 }}>{t('admin.handover_hint')}</span>

      <div className="row">
        <span className="muted">{t('admin.handover_current')}</span>
        <span className="mono" style={{ fontSize: 11 }}>
          {admin === null ? '…' : shortAddress(admin)}
        </span>
      </div>
      <div className="row">
        <span className="muted">{t('admin.handover_next')}</span>
        <span className="mono" style={{ fontSize: 11 }}>
          {offered === null ? t('admin.handover_none') : shortAddress(offered)}
        </span>
      </div>

      {mine && (
        <>
          <span className="label" style={{ color: 'var(--lime-text)' }}>{t('admin.handover_yours')}</span>
          <button className="btn" disabled={!connected} onClick={() => setConfirm('accept')}>
            {t('admin.handover_accept')}
          </button>
        </>
      )}

      <input
        className="mono"
        style={FIELD}
        placeholder={t('admin.handover_address')}
        value={heir}
        onChange={(e) => setHeir(e.target.value.trim())}
      />
      <button
        className="btn"
        disabled={!connected || heir.length === 0}
        onClick={() => setConfirm('offer')}
      >
        {t('admin.handover_offer')}
      </button>
      {offered !== null && (
        <button className="btn btn-ghost" disabled={!connected} onClick={() => setConfirm('cancel')}>
          {t('admin.handover_cancel')}
        </button>
      )}

      {confirm !== null && (
        <Sheet
          title={confirm === 'accept' ? t('confirm.handover_accept') : t('confirm.handover')}
          confirmLabel={
            confirm === 'accept'
              ? t('admin.handover_accept')
              : confirm === 'cancel'
                ? t('admin.handover_cancel')
                : t('admin.handover_offer')
          }
          danger={confirm !== 'offer'}
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            if (confirm === 'accept') onSend(buildAcceptAdmin());
            else if (confirm === 'cancel') onSend(buildSetNextAdmin(null));
            else onSend(buildSetNextAdmin(heir));
            setConfirm(null);
            setHeir('');
          }}
        >
          <span className="muted" style={{ fontSize: 13 }}>
            {confirm === 'accept' ? t('confirm.handover_accept_body') : t('confirm.handover_body')}
          </span>
          {confirm === 'offer' && (
            <span className="mono" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{heir}</span>
          )}
        </Sheet>
      )}
    </div>
  );
}

/** Как называется раскладка айтема для человека. */
const LAYOUT_NAME: Record<ItemLayout, string> = {
  0: 'TEP-62 · index 64',
  1: 'index 256',
  2: 'ссылка · гифты',
};

/** Сходятся ли ключи. Строка с сервера — hex с префиксом, в контракте — число. */
function keysMatch(inContract: bigint, onServer: string | null): boolean {
  return onServer !== null && BigInt(onServer) === inContract && inContract !== 0n;
}

/**
 * Вывод комиссии.
 *
 * Комиссия копится на самом мастере, и до появления этой команды достать её
 * было нечем: мастер переводов не делал вовсе. Баланс читается из сети, а не
 * считается из сделок, — показывать расчётное там, где человек нажимает
 * «вывести», значит однажды показать не ту сумму.
 */
function Withdraw({
  masterAddress,
  connected,
  onSend,
}: {
  masterAddress: string;
  connected: boolean;
  onSend: (body: Parameters<typeof toTxMessage>[2]) => void;
}) {
  const { t } = useTranslation();
  const { sum, money } = useFmt();
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [confirm, setConfirm] = useState(false);

  // Комиссия копится на балансе самого мастера: валюта одна и родная.
  const balance = useQuery({
    queryKey: ['masterFees', masterAddress],
    queryFn: () => getTonBalance(masterAddress),
    enabled: masterAddress.length > 0,
    retry: false,
  });

  const nano = (() => {
    const parsed = Number(amount.replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? BigInt(Math.round(parsed * 1e9)) : 0n;
  })();

  return (
    <div className="card col">
      <div className="label">{t('admin.withdraw')}</div>
      <span className="muted" style={{ fontSize: 12 }}>{t('admin.withdraw_hint')}</span>

      <div className="row">
        <span className="muted">{t('admin.withdraw_available')}</span>
        <strong className="num">
          {balance.data == null ? '—' : sum(balance.data)}
        </strong>
      </div>

      <input
        className="mono"
        style={FIELD}
        placeholder={t('admin.withdraw_to')}
        value={to}
        onChange={(e) => setTo(e.target.value.trim())}
      />
      <div className="row" style={{ gap: 8 }}>
        <input
          className="mono"
          style={{ ...FIELD, flex: 1 }}
          placeholder={t('admin.withdraw_amount')}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <button
          className="btn btn-ghost btn-sm"
          style={{ width: 'auto' }}
          disabled={balance.data == null}
          onClick={() => setAmount(String(Number(balance.data ?? 0n) / 1e9))}
        >
          {t('admin.withdraw_all')}
        </button>
      </div>

      {confirm && (
        <Sheet
          title={t('confirm.withdraw')}
          confirmLabel={`${t('admin.withdraw_send')} · ${money(nano)}`}
          onClose={() => setConfirm(false)}
          onConfirm={() => {
            setConfirm(false);
            onSend(buildWithdrawFees({ to, amount: nano }));
            setAmount('');
          }}
        >
          <span className="muted" style={{ fontSize: 13 }}>{t('confirm.withdraw_body')}</span>
          <span className="mono" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{to}</span>
        </Sheet>
      )}

      <button
        className="btn"
        disabled={!connected || to.length === 0 || nano <= 0n}
        onClick={() => setConfirm(true)}
      >
        {t('admin.withdraw_send')}
      </button>
    </div>
  );
}

/**
 * Панель управления.
 *
 * Показывается только админскому кошельку мастера — адрес читается из
 * контракта, а не из настроек приложения. Это не защита: защита в том, что
 * контракт отвергнет чужую команду. Это способ не показывать кнопки,
 * которые всё равно не сработают.
 */
export function Admin({
  masterAddress,
  isAdmin,
  isHeir,
  admin,
}: {
  masterAddress: string;
  isAdmin: boolean;
  /** Кошелёк, которому предложено принять администрирование. */
  isHeir: boolean;
  admin: string | null;
}) {
  const { t } = useTranslation();
  const { sum, money, dateTime } = useFmt();
  const actions = useActions();
  const errorText = useErrorText();
  const nameOf = useCollectionNames();

  const [feeBps, setFeeBps] = useState(MAX_FEE_BPS);
  const [confirmFee, setConfirmFee] = useState(false);
  const [removing, setRemoving] = useState<{ address: string } | null>(null);
  const [confirmKey, setConfirmKey] = useState<bigint | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const stats = useQuery({ queryKey: ['stats'], queryFn: fetchStats, enabled: isAdmin });

  // Реестр берётся из открытого эндпоинта, а не из админского: токена
  // веб-панели у приложения нет и быть не может, а без списка админ не
  // видит, что он уже внёс, и вносит второй раз.
  const registry = useQuery({ queryKey: ['registry'], queryFn: fetchRegistry, enabled: isAdmin });

  // Ключ приглашений читается из двух источников сразу — в этом весь смысл
  // карточки: расхождение не проявляется никак, кроме исчезнувшего дохода.
  const serverKey = useQuery({
    queryKey: ['refPubkeyServer'],
    queryFn: fetchReferralPubkey,
    enabled: isAdmin,
  });
  const contractKey = useQuery({
    queryKey: ['refPubkeyContract', masterAddress],
    queryFn: () => getMasterRefPubkey(masterAddress),
    enabled: isAdmin && masterAddress.length > 0,
    retry: false,
  });

  const send = (body: Parameters<typeof toTxMessage>[2]) => {
    setErr(null);
    void actions
      .sendToMaster(masterAddress, ADMIN_MESSAGE_TON, body)
      .then(() => setSent(true))
      .catch((e: unknown) => {
        const text = errorText(e);
        if (text.length > 0) setErr(text);
      });
  };

  // Преемник видит только передачу: прав у него ещё нет, и показывать ему
  // настройки значило бы показывать кнопки, которые контракт отвергнет.
  if (!isAdmin) {
    return (
      <div className="screen">
        {isHeir ? (
          <Handover
            masterAddress={masterAddress}
            admin={admin}
            myAddress={actions.myAddress}
            connected={actions.connected}
            onSend={send}
          />
        ) : (
          <div className="card muted">{t('admin.not_admin')}</div>
        )}
      </div>
    );
  }

  const data = stats.data;

  return (
    <div className="screen">
      <div className="col" style={{ gap: 2 }}>
        <strong style={{ fontSize: 17 }}>{t('admin.title')}</strong>
        <span className="muted" style={{ fontSize: 13 }}>{t('admin.subtitle')}</span>
      </div>

      {err !== null && <div className="card danger">{err}</div>}
      {sent && (
        <div className="card col">
          <span className="label" style={{ color: 'var(--lime-text)' }}>{t('state.sent_waiting')}</span>
          <span className="muted" style={{ fontSize: 12 }}>{t('state.sent_waiting_hint')}</span>
        </div>
      )}

      <div className="card col">
        <div className="label">{t('admin.contract')}</div>
        <div className="row">
          <span className="muted">{t('admin.master')}</span>
          <span className="mono" style={{ fontSize: 11 }}>{shortAddress(masterAddress)}</span>
        </div>
        <div className="row">
          <span className="muted">{t('admin.fee')}</span>
          <span className="num">
            {((registry.data?.feeBps ?? MAX_FEE_BPS) / 100).toFixed(2)}%
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={MAX_FEE_BPS}
          step={25}
          value={feeBps}
          onChange={(e) => setFeeBps(Number(e.target.value))}
        />
        <div className="row">
          <span className="muted">{t('admin.fee_apply')}</span>
          <span className="num">{(feeBps / 100).toFixed(2)}%</span>
        </div>
        <span className="muted" style={{ fontSize: 12 }}>{t('admin.fee_hint')}</span>

        {confirmFee && (
          <Sheet
            title={t('confirm.set_fee')}
            confirmLabel={`${t('admin.fee_apply')} · ${(feeBps / 100).toFixed(2)}%`}
            onClose={() => setConfirmFee(false)}
            onConfirm={() => {
              setConfirmFee(false);
              send(buildSetFee(feeBps));
            }}
          >
            <span className="muted" style={{ fontSize: 13 }}>{t('admin.fee_only_new')}</span>
          </Sheet>
        )}

        <button className="btn" disabled={!actions.connected} onClick={() => setConfirmFee(true)}>
          {t('admin.fee_apply')}
        </button>
      </div>

      {/* Реестр коллекций: то, чего у контракта не спросить перечислением —
          словарь get-методом не обойти, есть только проверка «эта в списке?».
          Валюты в реестре нет: она у протокола одна и родная. */}
      <div className="card col">
        <div className="label">{t('admin.collections')}</div>
        {(registry.data?.collections.length ?? 0) === 0 && (
          <span className="muted" style={{ fontSize: 13 }}>{t('admin.registry_empty')}</span>
        )}
        {registry.data?.collections.map((entry) => (
          <div key={entry.address} className="row">
            <span className="col" style={{ gap: 2, minWidth: 0 }}>
              <span className="mono" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>
                {nameOf(entry.address).name ?? shortAddress(entry.address)}
              </span>
              {/* Раскладка видна рядом: по ней сразу понятно, ту ли
                  коллекцию внесли и почему сделки по ней проходят. */}
              <span className="dim" style={{ fontSize: 10 }}>
                {t('admin.registry_layout')}: {LAYOUT_NAME[entry.layout as ItemLayout]}
              </span>
            </span>
            <button
              className="btn btn-ghost btn-sm"
              style={{ flex: 'none', width: 'auto' }}
              onClick={() => setRemoving({ address: entry.address })}
            >
              {t('admin.remove')}
            </button>
          </div>
        ))}
        <AddToRegistry onSend={(address, code, layout) => send(buildSetCollection(address, code, layout))} />
      </div>

      {removing !== null && (
        <Sheet
          title={t('confirm.registry_remove')}
          confirmLabel={t('admin.remove')}
          danger
          onClose={() => setRemoving(null)}
          onConfirm={() => {
            send(buildSetCollection(removing.address, null, 0));
            setRemoving(null);
          }}
        >
          <span className="muted" style={{ fontSize: 13 }}>{t('confirm.registry_remove_body')}</span>
          <span className="mono" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{removing.address}</span>
        </Sheet>
      )}

      {/* Ключ приглашений. Карточка существует ради одной строки — «совпадают»
          или «нет»: разошедшийся ключ не даёт ни ошибки, ни отказа, только
          тихо исчезнувший доход приглашающих. */}
      <div className="card col">
        <div className="label">{t('admin.ref_key')}</div>
        <span className="muted" style={{ fontSize: 12 }}>{t('admin.ref_key_hint')}</span>

        <div className="row">
          <span className="muted">{t('admin.ref_key_in_contract')}</span>
          <span className="mono" style={{ fontSize: 11 }}>
            {contractKey.data === undefined
              ? '…'
              : contractKey.data === 0n
                ? t('admin.ref_key_none')
                : `0x${contractKey.data.toString(16).padStart(64, '0').slice(0, 8)}…`}
          </span>
        </div>
        <div className="row">
          <span className="muted">{t('admin.ref_key_on_server')}</span>
          <span className="mono" style={{ fontSize: 11 }}>
            {serverKey.data === undefined
              ? '…'
              : serverKey.data.pubkey === null
                ? t('admin.ref_key_none')
                : `${serverKey.data.pubkey.slice(0, 10)}…`}
          </span>
        </div>

        {contractKey.data !== undefined && serverKey.data !== undefined && (
          <span
            className={keysMatch(contractKey.data, serverKey.data.pubkey) ? 'label' : 'danger'}
            style={{ fontSize: 12, color: keysMatch(contractKey.data, serverKey.data.pubkey) ? 'var(--lime-text)' : undefined }}
          >
            {keysMatch(contractKey.data, serverKey.data.pubkey)
              ? t('admin.ref_key_match')
              : t('admin.ref_key_mismatch')}
          </span>
        )}

        <button
          className="btn"
          disabled={!actions.connected || serverKey.data?.pubkey == null}
          onClick={() => setConfirmKey(BigInt(serverKey.data?.pubkey ?? '0'))}
        >
          {t('admin.ref_key_apply')}
        </button>
        <button
          className="btn btn-ghost"
          disabled={!actions.connected || contractKey.data === 0n}
          onClick={() => setConfirmKey(0n)}
        >
          {t('admin.ref_key_off')}
        </button>
      </div>

      {confirmKey !== null && (
        <Sheet
          title={t('confirm.set_ref_key')}
          confirmLabel={confirmKey === 0n ? t('admin.ref_key_off') : t('admin.ref_key_apply')}
          danger={confirmKey === 0n}
          onClose={() => setConfirmKey(null)}
          onConfirm={() => {
            send(buildSetRefPubkey(confirmKey));
            setConfirmKey(null);
          }}
        >
          <span className="muted" style={{ fontSize: 13 }}>{t('confirm.set_ref_key_body')}</span>
        </Sheet>
      )}

      <Handover
        masterAddress={masterAddress}
        admin={admin}
        myAddress={actions.myAddress}
        connected={actions.connected}
        onSend={send}
      />

      <Withdraw
        masterAddress={masterAddress}
        connected={actions.connected}
        onSend={send}
      />

      <div className="card col">
        <div className="label">{t('admin.deal_code')}</div>
        <span className="muted" style={{ fontSize: 13 }}>{t('admin.deal_code_hint')}</span>
        <div className="row">
          <span className="muted">
            {registry.data?.dealCodeChangedAt == null
              ? t('admin.deal_code_never')
              : t('admin.deal_code_changed')}
          </span>
          <span className="num">
            {registry.data?.dealCodeChangedAt == null
              ? '—'
              : dateTime(registry.data.dealCodeChangedAt)}
          </span>
        </div>
      </div>

      <div className="card col">
        <div className="label">{t('admin.stats')}</div>
        {stats.isLoading && <div className="skeleton" style={{ minHeight: 80 }} />}
        {data !== undefined && (
          <>
            <div className="row">
              <span className="muted">{t('admin.volume')}</span>
              <strong className="num"><Money value={data.volume} /></strong>
            </div>
            <div className="row">
              <span className="muted">{t('admin.retained')}</span>
              <span className="num">{sum(data.retained)}</span>
            </div>
            <div className="label" style={{ marginTop: 6 }}>{t('admin.deals_by_state')}</div>
            {(['0', '1', '2', '3'] as const).map((state) => (
              <div key={state} className="row">
                <span className="muted">{t(`admin.state_${state}`)}</span>
                <span className="num">{data.deals[state] ?? 0}</span>
              </div>
            ))}
            <div className="row">
              <span className="muted">{t('admin.requests_open')}</span>
              <span className="num">{(data.requests['open'] ?? 0) + (data.requests['countered'] ?? 0)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
